/**
 * dcent-sticky-buy-bar.js — 페이지 하단 고정 구매 바의 표시 제어 + 구매 컨트롤러 연결.
 *
 * 이 파일은 담기를 구현하지 않는다. 담기·옵션 상태는 `assets/dcent-pdp-buy.js` 가 전담한다
 * (drop-04 계약 §3). 다만 고정 바는 히어로 섹션 **밖**(형제)에 있어 히어로 루트에 걸린 클릭
 * 리스너에 닿지 않으므로, 바의 버튼 클릭을 컨트롤러가 문서 레벨로 받는 이벤트
 * `dcent:pdp:request-add` 로 넘긴다. 즉 여기 있는 것은 구현이 아니라 다리다.
 *
 * 하는 일 네 가지:
 *   1. 표시 — window.scrollY > window.innerHeight*0.6 이면 .is-shown (히어로 구매 버튼과 중복 방지).
 *      모바일 상시 표시(always_on_mobile)는 CSS 가 data-always-on-mobile 로 처리한다.
 *   2. 모드 — 구매 컨트롤러가 있는 페이지면 data-buy-mode="cart"(담기 버튼 2종),
 *      없으면 마크업 기본값 "link" 그대로(링크아웃 폴백). 설정이 아니라 페이지 상태로 갈린다.
 *      판정은 두 갈래다: 컨트롤러가 붙는 마크업이 있으면 즉시 전환(깜빡임 방지),
 *      컨트롤러가 실제로 살아나며 내보내는 `dcent:pdp:state` 를 받으면 그때도 전환(늦은 확인).
 *   3. 실패 문구 — 컨트롤러의 에러 표시는 히어로 루트 안만 훑으므로, 바 자신의 자리에
 *      dcent:cart:error 를 받아 문구를 띄운다(조용한 실패 금지).
 *   4. 폭 실측(drop-07) — 담기·바로구매의 텍스트↔아이콘 전환을 브레이크포인트가 아니라
 *      라벨 실측으로 정한다. `data-add-compact` / `data-buy-compact` 를 토글하고 CSS 가 반응한다.
 *
 * 소스: ~/work/dcent-design-drops/drop-07-20260814/src/components/StickyBuyBar.tsx
 *       (측정 엔진 원형은 그 파일의 ResizeObserver `check()`)
 *
 * Markup:
 *   <section class="dcent-sticky-buy-bar" data-sticky-buy-bar data-buy-mode="link"> ... </section>
 * page-wide 단일 인스턴스지만 idempotent guard로 이중 바인딩을 방지한다(안전).
 */

export function initStickyBuyBar(root = document) {
  root.querySelectorAll("[data-sticky-buy-bar]").forEach((el) => {
    if (el.dataset.stickyBuyBarReady === "true") return;
    el.dataset.stickyBuyBarReady = "true";

    const onScroll = () => {
      el.classList.toggle(
        "is-shown",
        window.scrollY > window.innerHeight * 0.6,
      );
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const errorEl = el.querySelector("[data-sticky-buy-bar-error]");
    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message || el.dataset.errorText || "";
      errorEl.hidden = !errorEl.textContent;
    };
    const clearError = () => {
      if (!errorEl) return;
      errorEl.textContent = "";
      errorEl.hidden = true;
    };

    // 담기 모드 전환 — 컨트롤러가 있는 페이지에서만. 없으면 링크아웃 폴백이 그대로 남는다.
    const enableCartMode = () => {
      el.dataset.buyMode = "cart";
    };
    document.addEventListener("dcent:pdp:state", enableCartMode);
    if (
      document.querySelector("[data-dcent-pdp-buy] [data-dcent-buy-context]")
    ) {
      enableCartMode();
    }

    // 담기와 바로 구매를 각각 컨트롤러에 넘긴다. 결제 이동은 컨트롤러가 판단한다 —
    // 여기서 "담기 요청 → dcent:cart:added 를 보고 이동" 으로 엮으면 다른 곳에서 담아도
    // 이 바가 결제로 끌고 가는 오작동이 생긴다.
    el.addEventListener("click", (e) => {
      const buyNow = e.target.closest("[data-dcent-buy-now]");
      const add = buyNow ? null : e.target.closest("[data-dcent-buy-add]");
      if (!buyNow && !add) return;
      e.preventDefault();
      clearError();
      document.dispatchEvent(
        new CustomEvent(
          buyNow ? "dcent:pdp:request-buy-now" : "dcent:pdp:request-add",
        ),
      );
    });

    document.addEventListener("dcent:cart:error", (e) => {
      showError(e.detail && e.detail.message);
    });

    // ── 폭 실측 — 버튼 축소 순서를 정한다(원본 §21-3) ────────────────────────────
    // 브레이크포인트로 가르면 긴 상품명·긴 라벨(일본어·중국어)에서 그대로 넘친다. 그래서 라벨의
    // 자연 폭을 히든 스팬으로 재고 바 안쪽 폭과 견준다. 양보 순서는
    //   ① 담기 텍스트 → 아이콘  ② 상품명 말줄임  ③ 최후에 바로 구매도 아이콘
    // 이라, 금액이 붙은 바로 구매 라벨이 가장 오래 남는다.
    //
    // 상수는 원본 그대로다 — 썸네일 48 / info gap 12 / 좌우 그룹 gap 16 / 버튼 사이 gap 8 /
    // 아이콘 버튼 40 / 여유 8. 담기 텍스트 버튼은 체크아이콘 18 + gap 6 = 24 에 좌우 패딩 40.
    // 기준 폭은 `__inner.clientWidth`(패딩 포함) 로, 원본 barRef(px-4 포함)와 같은 자리다.
    const inner = el.querySelector(".dcent-sticky-buy-bar__inner");
    const nameM = el.querySelector(".dcent-sticky-buy-bar__measure--name");
    const addedM = el.querySelector(".dcent-sticky-buy-bar__measure--added");
    const buyM = el.querySelector(".dcent-sticky-buy-bar__measure--buy");

    if (inner && nameM && buyM) {
      const measureFit = () => {
        const buyW = buyM.offsetWidth + 40;
        const addTextW = (addedM ? addedM.offsetWidth : 0) + 24 + 40;
        const fullName = 48 + 12 + nameM.offsetWidth + 16 + 8 + buyW + 8;
        el.dataset.addCompact = String(inner.clientWidth < fullName + addTextW);
        // 상품명을 폭 0 까지 양보시켜도 안 들어갈 때만 바로 구매가 아이콘이 된다.
        const noName = 48 + 12 + 16 + 8 + buyW + 8;
        el.dataset.buyCompact = String(inner.clientWidth < noName + 40);
      };

      if (typeof ResizeObserver === "function") {
        const ro = new ResizeObserver(() => measureFit());
        ro.observe(inner);
        ro.observe(nameM);
        ro.observe(buyM);
      } else {
        window.addEventListener("resize", measureFit, { passive: true });
      }

      // 옵션·애드온을 바꾸면 상품명과 합계 금액이 바뀌어 잰 폭도 달라진다.
      // 컨트롤러가 DOM 을 갱신한 **다음** 프레임에 다시 잰다.
      document.addEventListener("dcent:pdp:state", () =>
        window.requestAnimationFrame(measureFit),
      );

      // 벤더 폰트(Funnel Sans · Pretendard)가 늦게 적용되면 글자 폭이 바뀐다 — 적용 후 한 번 더.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measureFit, () => {});
      }

      measureFit();
    }
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initStickyBuyBar());
  // 테마 편집기가 섹션 DOM을 AJAX 교체할 때 재초기화(라이브 스토어는 미발생). e.target 스코프로 재실행.
  document.addEventListener("shopify:section:load", (e) =>
    initStickyBuyBar(e.target),
  );
}
